import AppStoreBuildComponent from '@/components/appStoreBuilds/AppStoreBuildComponent';
import WorkflowsPlatformIcon from '@/components/icons/WorkflowsPlatformIcon';
import { antGrey } from '@/components/styles/globalStyles';
import { displayTextForAppPlatform } from '@/helpers/displayHelpers';
import { AppStoreAvailableBuild } from '@/types';
import { Destination } from '@/types/destinations';
import Divider from 'antd/lib/divider';
import Avatar from 'antd/lib/avatar';
import List from 'antd/lib/list';
import Popover from 'antd/lib/popover';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';

export interface WorkflowRunDestinationBuildsWidgetProps {
    allDestinations: Destination[];
    selectedDestinationStepIds: string[];
    appStoreBuildsByDestinationStepId: Record<string, AppStoreAvailableBuild[]>;
    styleOverrides?: React.CSSProperties;
}

interface DestinationBuildsDataSourceItem {
    destinationStepId: string;
    destination: Destination;
    destinationBuilds: AppStoreAvailableBuild[];
}

export const WorkflowRunDestinationBuildsWidget: React.FC<WorkflowRunDestinationBuildsWidgetProps> = ({
    allDestinations,
    selectedDestinationStepIds,
    appStoreBuildsByDestinationStepId,
    styleOverrides,
}) => {
    const destinationBuildsDataSource: DestinationBuildsDataSourceItem[] = React.useMemo(() => {
        const haveBuildsDataSource: DestinationBuildsDataSourceItem[] = [];
        const noBuildsDataSource: DestinationBuildsDataSourceItem[] = [];

        for (const destinationStepId of selectedDestinationStepIds) {
            const matchingDestination = allDestinations.find((dest) => dest.stepConfigId === destinationStepId);
            const matchingBuilds =
                appStoreBuildsByDestinationStepId ? appStoreBuildsByDestinationStepId[destinationStepId] : [];

            const haveMatchingDestination = !!matchingDestination;
            const haveMatchingBuilds = !!matchingBuilds && matchingBuilds.length > 0;

            if (haveMatchingDestination) {
                if (haveMatchingBuilds) {
                    haveBuildsDataSource.push({
                        destinationStepId,
                        destination: matchingDestination,
                        destinationBuilds: matchingBuilds,
                    });
                } else {
                    noBuildsDataSource.push({
                        destinationStepId,
                        destination: matchingDestination,
                        destinationBuilds: [],
                    });
                }
            }
        }

        return [...haveBuildsDataSource, ...noBuildsDataSource];
    }, [allDestinations, selectedDestinationStepIds, appStoreBuildsByDestinationStepId]);

    const buildsCount = destinationBuildsDataSource.reduce((totalCount, datum) => {
        return totalCount + datum.destinationBuilds.length;
    }, 0);

    let buildsDisplayString: string;
    if (buildsCount > 0) {
        buildsDisplayString = buildsCount > 1 ? `${buildsCount} builds` : '1 build';
    } else {
        buildsDisplayString = 'No builds';
    }

    const popoverContent = <DestinationMatchedBuildsList destinationBuildsDataSource={destinationBuildsDataSource} />;

    if (buildsCount === 1) {
        const singleDestinationBuilds = destinationBuildsDataSource[0]?.destinationBuilds ?? [];
        const visibleBuilds = singleDestinationBuilds.slice(0, 2);
        const remainingCount = singleDestinationBuilds.length - visibleBuilds.length;

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, ...(styleOverrides ?? {}) }}>
                {singleDestinationBuilds.length > 0 ?
                    <Space size={4} wrap={false}>
                        {visibleBuilds.map((appStoreBuild) => (
                            <AppStoreBuildComponent
                                key={appStoreBuild.appStoreBuild.id}
                                {...appStoreBuild}
                                styleOverrides={{
                                    border: `1px solid ${antGrey.grey5}`,
                                    fontWeight: 300,
                                }}
                            />
                        ))}
                        {remainingCount > 0 && (
                            <Text style={{ color: antGrey.grey6, fontSize: 12 }}>+{remainingCount} more</Text>
                        )}
                    </Space>
                :   <Text
                        style={{
                            color: antGrey.grey7,
                            fontSize: 14,
                            fontWeight: 300,
                        }}
                    >
                        No builds found.
                    </Text>
                }
            </div>
        );
    }

    return (
        <Popover content={popoverContent}>
            <Tag
                style={{
                    color: antGrey.grey7,
                    fontSize: 14,
                    padding: '4px 7px',
                    margin: 0,
                    border: `1px solid ${antGrey.grey5}`,
                    ...styleOverrides,
                }}
                color={antGrey.grey3}
            >
                {buildsDisplayString}
            </Tag>
        </Popover>
    );
};

export interface DestinationMatchedBuildsListProps {
    destinationBuildsDataSource: DestinationBuildsDataSourceItem[];
}

export const DestinationMatchedBuildsList: React.FC<DestinationMatchedBuildsListProps> = ({
    destinationBuildsDataSource,
}) => {
    return (
        <List
            style={{
                maxHeight: '330px',
                overflowY: 'scroll',
                padding: '6px 6px 6px 6px',
                minWidth: '100px',
            }}
            split={false}
            dataSource={destinationBuildsDataSource}
            renderItem={(destBuildDatum, i) => {
                const isLast = i === destinationBuildsDataSource.length - 1;
                const shouldRenderDivider = destinationBuildsDataSource.length > 1 && !isLast;

                const destination = destBuildDatum.destination;
                const destinationStoreBuilds = destBuildDatum.destinationBuilds;
                const haveBuilds = destinationStoreBuilds.length > 0;

                return (
                    <List.Item
                        key={destBuildDatum.destination.stepConfigId}
                        style={{
                            padding: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'start',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'start',
                                width: '100%',
                                gap: 16,
                            }}
                        >
                            <Text
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 8,
                                    color: antGrey.grey8,
                                    fontSize: 14,
                                    fontWeight: 300,
                                }}
                            >
                                {destination.iconURL ?
                                    <Avatar shape={'square'} size={24} src={destination.iconURL} />
                                :   <WorkflowsPlatformIcon platform={destination.platform} width={24} height={24} />}
                                {destination.name ?
                                    destination.name
                                :   displayTextForAppPlatform(destination.platform, destination.appStoreType)}
                            </Text>
                            {haveBuilds && (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 8,
                                    }}
                                >
                                    {destinationStoreBuilds.map((appStoreBuild) => {
                                        return (
                                            <AppStoreBuildComponent
                                                key={appStoreBuild.appStoreBuild.id}
                                                {...appStoreBuild}
                                                styleOverrides={{
                                                    border: `1px solid ${antGrey.grey5}`,
                                                    fontWeight: 300,
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                            {!haveBuilds && (
                                <Text
                                    style={{
                                        color: antGrey.grey7,
                                        fontWeight: 300,
                                    }}
                                >
                                    No builds found.
                                </Text>
                            )}
                        </div>
                        {shouldRenderDivider && (
                            <Divider
                                style={{
                                    margin: '16px 0px',
                                }}
                            />
                        )}
                    </List.Item>
                );
            }}
        />
    );
};
